import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { getStaticVillas, getLiveVillas } from '../lib/api';
import VillaCard from '../components/VillaCard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const CatalogPage = () => {
  const { t, language } = useLanguage();
  const [villas, setVillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('default');
  const liveDataFetched = useRef(false);

  useEffect(() => {
    loadVillas();
  }, []);

  // SWR Pattern: Static first, then live API in background
  const loadVillas = async () => {
    // Step 1: Load static data immediately
    const staticData = await getStaticVillas();
    if (staticData && staticData.length > 0) {
      setVillas(staticData);
      setLoading(false);
    }
    
    // Step 2: Fetch live data in background (only once)
    if (!liveDataFetched.current) {
      liveDataFetched.current = true;
      const liveData = await getLiveVillas();
      if (liveData && liveData.length > 0) {
        // Only update if data is different (to avoid unnecessary re-renders)
        setVillas(prevVillas => {
          const isDifferent = JSON.stringify(prevVillas) !== JSON.stringify(liveData);
          return isDifferent ? liveData : prevVillas;
        });
      }
      setLoading(false);
    }
  };

  const sortedVillas = [...villas].sort((a, b) => {
    if (sortBy === 'price-asc') return a.price - b.price;
    if (sortBy === 'price-desc') return b.price - a.price;
    return 0;
  });

  return (
    <>
      <Helmet>
        <title>{String(t('seo.catalogTitle'))}</title>
        <meta name="description" content={String(t('seo.catalogDescription'))} />
        <link rel="canonical" href="https://villa.ooo/catalog" />
      </Helmet>

      {/* Header */}
      <section className="pt-36 pb-8 md:pt-44 md:pb-10 bg-bone" data-testid="catalog-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="heading-section font-heading text-night">{language === 'ru' ? 'Каталог' : 'Catalog'}</h1>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-4 border-b border-gray-200" data-testid="catalog-filters">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <p className="text-text-secondary">
              {villas.length} {language === 'ru' ? 'объектов' : 'properties'}
            </p>
            
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[200px] border-gray-200" data-testid="sort-select">
                <SelectValue placeholder={t('catalog.sort')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">{t('catalog.all')}</SelectItem>
                <SelectItem value="price-asc">{t('catalog.priceAsc')}</SelectItem>
                <SelectItem value="price-desc">{t('catalog.priceDesc')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Villas Grid */}
      <section className="py-8 md:py-12 bg-bone" data-testid="catalog-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/3] bg-gray-200 mb-6" />
                  <div className="h-4 bg-gray-200 w-1/3 mb-3" />
                  <div className="h-8 bg-gray-200 w-2/3 mb-3" />
                  <div className="h-4 bg-gray-200 w-1/2" />
                </div>
              ))}
            </div>
          ) : sortedVillas.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              {sortedVillas.map((villa, index) => (
                <VillaCard key={villa.id} villa={villa} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-text-secondary text-lg">{t('villas.noVillas')}</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default CatalogPage;
